import type { FC } from 'react'

import { Input, InputWithValidation, Select } from '@skytech/ui'
import {
  COUNTRY_FLAGS,
  MAX_PHONE_NUMBER_LENGTH,
  countryOptions,
  getFormattedPhoneNumber,
} from '@skytech/utils'

import { Translation } from '@/app/translations/Translation'
import { validateEmail } from '@/util/formatters/text'

import { useNewSubscriptionContext } from './NewSubscriptionForm.context'

import './NewSubscriptionUser.styles.scss'

const NewSubscriptionUser: FC = () => {
  const newSub = useNewSubscriptionContext()
  const [name, setName] = newSub.name
  const [email, setEmail] = newSub.email
  const [employeeNumber, setEmployeeNumber] = newSub.employeeNumber
  const [contactNumber, setContactNumber] = newSub.contactNumber
  const [contactNumberCountryId, setContactNumberCountryId] = newSub.contactNumberCountryId
  const [subType] = newSub.subType
  const isBusinessSub = subType === 'business'

  return (
    <div className="new-subscription-user">
      <h3>{isBusinessSub ? <Translation id="Usage" /> : <Translation id="User" />}</h3>
      <div className="inputs">
        <div className="person">
          <Input
            isFullWidth
            marginBottom={16}
            id="individual-description"
            label={
              isBusinessSub ? <Translation id="Description" /> : <Translation id="Employee name" />
            }
            value={name}
            onChange={e => setName(e.target.value)}
          />

          {!isBusinessSub && (
            <Input
              id="new-sub-employee-number"
              isFullWidth
              label={<Translation id="Employee number" />}
              value={employeeNumber || ''}
              onChange={e => setEmployeeNumber(e.target.value)}
            />
          )}
        </div>

        <div className="contact">
          {!isBusinessSub && (
            <InputWithValidation
              isFullWidth
              id="current-email"
              marginBottom={16}
              label={<Translation id="Current e-mail" />}
              type="email"
              value={email}
              validator={validateEmail}
              errorMessage={<Translation id="Email format is invalid" />}
              onChange={e => setEmail(e.target.value)}
            />
          )}

          <div className="flex items-center justify-between gap-1">
            <Select
              label={<Translation id="Country code" />}
              id="contactNumberCountryId"
              value={contactNumberCountryId}
              options={countryOptions.map(option => ({
                value: option.value,
                label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
              }))}
              onChange={option => {
                if (typeof option?.value !== 'number')
                  throw Error('Wrong contact number country id value')
                setContactNumberCountryId(option?.value)

                if (contactNumber) {
                  const formattedNumber = getFormattedPhoneNumber({
                    value: contactNumber,
                    countryId: option?.value,
                  })
                  if (formattedNumber !== contactNumber) {
                    setContactNumber(formattedNumber)
                  }
                }
              }}
            />

            <Input
              id="new-sub-contact-number"
              isFullWidth
              wrapperClassName="grow"
              label={<Translation id="Contact number" />}
              value={contactNumber || ''}
              maxLength={MAX_PHONE_NUMBER_LENGTH}
              onChange={e => {
                const formattedNumber = getFormattedPhoneNumber({
                  value: e.target.value,
                  countryId: contactNumberCountryId,
                })
                setContactNumber(formattedNumber)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewSubscriptionUser
